// Client-side pin-and-flow redistribution — mirrors app/engine/rebalance.py.
// Pinned lines hold an exact quantity; unpinned lines flow to absorb the
// remainder proportional to their model weight. Total always equals nTotal.

export type RebLine = { key: string; weight: number; pin: number | null }

export type RebalanceResult = {
  quantities: Record<string, number>
  total: number
  locked: number
  status: 'ok' | 'overpinned' | 'fully_pinned_mismatch'
  message: string
}

// Distribute n units across shares proportionally; result sums exactly to n.
// Tie-break on remainder matches the Python largest_remainder (higher index wins).
export function largestRemainder(n: number, shares: number[]): number[] {
  if (!shares.length || n === 0) return shares.map(() => 0)
  const total = shares.reduce((a, b) => a + b, 0)
  if (total === 0) {
    const per = Math.floor(n / shares.length)
    const out = shares.map(() => per)
    out[0] += n - per * shares.length
    return out
  }
  const exact = shares.map(s => (s / total) * n)
  const floored = exact.map(Math.floor)
  let deficit = n - floored.reduce((a, b) => a + b, 0)
  const order = shares
    .map((_, i) => i)
    .sort((a, b) => (exact[b] - floored[b]) - (exact[a] - floored[a]) || b - a)
  for (let k = 0; k < deficit; k++) floored[order[k]] += 1
  return floored
}

export function rebalance(lines: RebLine[], nTotal: number): RebalanceResult {
  const pinned = lines.filter(l => l.pin !== null)
  const unpinned = lines.filter(l => l.pin === null)
  const locked = pinned.reduce((a, l) => a + (l.pin ?? 0), 0)
  const quantities: Record<string, number> = {}
  for (const l of pinned) quantities[l.key] = l.pin as number

  if (locked > nTotal) {
    for (const l of unpinned) quantities[l.key] = 0
    return {
      quantities, total: locked, locked, status: 'overpinned',
      message: `Pinned total ${locked} exceeds order size ${nTotal} by ${locked - nTotal}.`,
    }
  }

  if (unpinned.length === 0) {
    const ok = locked === nTotal
    return {
      quantities, total: locked, locked,
      status: ok ? 'ok' : 'fully_pinned_mismatch',
      message: ok ? '' : `Pinned total ${locked} does not match order size ${nTotal} (off by ${locked - nTotal}).`,
    }
  }

  const remainder = nTotal - locked
  const flowed = largestRemainder(remainder, unpinned.map(l => Math.max(l.weight, 0)))
  unpinned.forEach((l, i) => { quantities[l.key] = flowed[i] })
  return { quantities, total: locked + flowed.reduce((a, b) => a + b, 0), locked, status: 'ok', message: '' }
}
